
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { errorHandler } from "@/utils/errorHandler"
import { logger } from "@/utils/logger"

export const useMessages = (conversationId: string) => {
  return useQuery({
    queryKey: ["messages", conversationId],
    queryFn: async () => {
      if (!conversationId) return []

      try {
        logger.info("Carregando mensagens", "useMessages", { conversationId })

        const { data, error } = await supabase
          .from("messages")
          .select(`
            *,
            sender:profiles!messages_sender_id_fkey(
              id,
              full_name,
              role
            )
          `)
          .eq("conversation_id", conversationId)
          .eq("is_deleted", false)
          .order("created_at", { ascending: true })

        if (error) {
          logger.error("Erro ao buscar mensagens", "useMessages", error)
          errorHandler.handleError({
            code: 'MESSAGE_LOAD_ERROR',
            message: error.message,
            details: error
          })
          throw error
        }
        
        logger.info("Mensagens carregadas com sucesso", "useMessages", { 
          count: data?.length || 0 
        })
        
        return data || []
      } catch (error) {
        logger.error("Erro na query de mensagens", "useMessages", error)
        throw error
      }
    },
    enabled: !!conversationId,
    retry: 2
  })
}

export const useSendMessage = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (data: {
      conversation_id: string
      content: string
      message_type?: string
      file_url?: string
    }) => {
      if (!profile?.id) {
        const error = new Error("Usuário não autenticado")
        logger.error("Tentativa de envio sem autenticação", "useSendMessage")
        errorHandler.handleError({
          code: 'PERMISSION_DENIED',
          message: error.message
        })
        throw error
      }

      try {
        logger.info("Enviando mensagem", "useSendMessage", {
          conversationId: data.conversation_id,
          userId: profile.id
        })

        const { data: message, error } = await supabase
          .from("messages")
          .insert({
            ...data,
            sender_id: profile.id
          })
          .select()
          .single()

        if (error) {
          logger.error("Erro ao enviar mensagem", "useSendMessage", error)
          errorHandler.handleError({
            code: 'MESSAGE_SEND_ERROR',
            message: error.message,
            details: error
          })
          throw error
        }

        logger.info("Mensagem enviada com sucesso", "useSendMessage", { messageId: message.id })
        return message
      } catch (error) {
        logger.error("Erro na mutação de envio", "useSendMessage", error)
        throw error
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["messages", variables.conversation_id] })
      queryClient.invalidateQueries({ queryKey: ["conversations"] })
      logger.info("Queries invalidadas após envio", "useSendMessage")
    },
    onError: (error) => {
      logger.error("Erro no callback de erro", "useSendMessage", error)
    }
  })
}
